import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { KeyRound, Lock, Mail, PlayCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useDemoInfo } from '@/lib/demo';
import { ApiError } from '@/lib/api';
import { Alert, Button, Field, Input } from '@/components/ui';

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const demo = useDemoInfo();

  // Öffentliche Demo: Zugangsdaten vorbelegen
  useEffect(() => {
    if (demo?.enabled && demo.email && demo.password) {
      setEmail((v) => v || demo.email!);
      setPassword((v) => v || demo.password!);
    }
  }, [demo]);

  if (user) return <Navigate to={user.role.key === 'MIETER' ? '/portal' : user.role.key === 'HANDWERKER' ? '/handwerkerportal' : from} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await login(email, password, needsTotp ? totp : undefined);
      if (r.requiresTotp) {
        setNeedsTotp(true);
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Anmeldung fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden flex-1 flex-col justify-between bg-brand-900 p-10 text-white lg:flex">
        <img src="/brand/logo-light.png" alt="Mietverwaltung Portal – Klar und digital" className="h-auto w-full max-w-[380px]" width={380} height={45} />
        <div className="max-w-md">
          <h1 className="text-3xl font-semibold leading-tight">Mieten, Zahlungen und Mahnwesen – automatisch im Blick.</h1>
          <p className="mt-4 text-slate-300">
            Sollstellung, Bankabgleich, Soll/Ist-Vergleich, Erinnerung und Mahnung laufen jeden Tag automatisch. Sie greifen nur noch dort ein, wo es nötig ist.
          </p>
        </div>
        <p className="text-xs text-slate-400">Datenschutzkonform nach DSGVO · Hosting in der EU empfohlen</p>
      </div>
      <div className="flex flex-1 items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <img src="/brand/logo-dark.png" alt="Mietverwaltung Portal – Klar und digital" className="mx-auto mb-4 h-auto w-full max-w-[300px] lg:hidden" width={300} height={36} />
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">Anmelden</h2>
            <p className="mt-1 text-sm text-slate-500">Bitte melden Sie sich mit Ihrem Benutzerkonto an.</p>
          </div>
          {demo?.enabled && (
            <Alert tone="info" title="Live-Demo">
              Die Zugangsdaten sind bereits eingetragen – einfach auf „Anmelden“ klicken. Alle Daten sind fiktiv, Änderungen sind in der Demo deaktiviert.
              {demo.accounts && demo.accounts.length > 1 ? (
                <span className="mt-2 block">
                  <span className="block text-xs font-medium">Ansicht wählen:</span>
                  <span className="mt-1 flex flex-wrap gap-1.5">
                    {demo.accounts.map((a) => (
                      <button
                        key={a.key}
                        type="button"
                        onClick={() => {
                          setEmail(a.email);
                          setPassword(a.password);
                        }}
                        className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition ${email === a.email ? 'border-brand-600 bg-brand-600 text-white' : 'border-brand-200 bg-white text-brand-800 hover:bg-brand-50'}`}
                      >
                        <PlayCircle className="h-3.5 w-3.5" /> {a.label}
                      </button>
                    ))}
                  </span>
                  <span className="mt-1.5 block text-xs text-slate-600">
                    {demo.accounts.find((a) => a.email === email)?.key === 'MIETER'
                      ? 'Als Mieter sehen Sie Ihre Schadensmeldungen: Terminvorschläge wählen und erledigte Arbeiten bestätigen.'
                      : demo.accounts.find((a) => a.email === email)?.key === 'HANDWERKER'
                        ? 'Als Handwerker sehen Sie Aufträge der Hausverwaltung – mit Annahme, Termin, Fotos, Bericht und Preis.'
                        : 'Als Hausverwaltung steuern Sie Schäden, Handwerker-Aufträge, Mieter und Zahlungen.'}
                  </span>
                </span>
              ) : (
                demo.email && (
                  <span className="mt-1 flex items-center gap-1.5 text-xs">
                    <PlayCircle className="h-3.5 w-3.5" /> {demo.email} · {demo.password}
                  </span>
                )
              )}
            </Alert>
          )}
          {error && <Alert tone="error">{error}</Alert>}
          {!needsTotp ? (
            <>
              <Field label="E-Mail-Adresse" htmlFor="email">
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" autoFocus />
                </div>
              </Field>
              <Field label="Passwort" htmlFor="password">
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9" />
                </div>
              </Field>
              {!demo?.enabled && (
                <div className="-mt-3 text-right">
                  <Link to="/passwort-vergessen" className="text-sm font-medium text-brand-700 hover:underline">
                    Passwort vergessen?
                  </Link>
                </div>
              )}
            </>
          ) : (
            <Field label="Bestätigungscode (Authenticator-App)" htmlFor="totp" hint="6-stelliger Code oder Wiederherstellungscode">
              <div className="relative">
                <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="totp" inputMode="numeric" autoComplete="one-time-code" required value={totp} onChange={(e) => setTotp(e.target.value)} className="pl-9 tracking-widest" autoFocus />
              </div>
            </Field>
          )}
          <Button type="submit" variant="primary" className="w-full" loading={busy}>
            {needsTotp ? 'Bestätigen' : 'Anmelden'}
          </Button>
          {needsTotp && (
            <button type="button" className="w-full text-center text-sm text-slate-500 hover:underline" onClick={() => { setNeedsTotp(false); setTotp(''); }}>
              Zurück
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
